import React, { useState, useEffect } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { success, failure } from '~/redux/actions/snackbar-actions';
import {
  createContact,
  fetchContactLabels,
  fetchContacts,
} from '~/redux/actions/contacts-actions.js';

//Utils
import { MESSAGES } from '~/utils/message.js';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';
import { EMAIL_REGEX } from '~/utils/constants.js';

// Third Party Libraries

// Components
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  Box,
  Grid,
  InputLabel,
  CircularProgress,
  Input,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Divider,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Components
import ModalInput from '../CreateContact/ModalInput/index.jsx';
import CreateTagModal from '~/components/Settings/Tags/CreateTag/index.jsx';

//MUI Component
import { Container } from '@mui/material';

// Icons
import {
  contactInfo2CIcon,
  tooltipCIcon,
  mailingInfoCIcon,
  propertyInfoCIcon,
  UncheckedCIcon,
  CheckedCIcon,
} from '~/assets/images';
import DoneIcon from '@mui/icons-material/Done';
// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlusIconCIcon from '~/assets/images/create-orders/plus-icon.svg';

// Styles
import './styles.scss';

const CreateContactModal = (props) => {
  const formId = 'create-contact-form';
  const { handleClose, open } = props;
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createTagModal, setCreateTagModal] = useState(false);
  const [isDncList, setIsDncList] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isPropertyAddressEnabled = useSelector(
    (state) =>
      state?.generalSettingsReducers?.generalSettings?.propertyAddressStrictness === 'Include' ||
      false
  );

  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .test({
        test: function (value) {
          const { companyName } = this.parent;
          return !!value || !!companyName;
        },
        message: MESSAGES.CONTACTS.CREATE.FIRST_NAME_OR_COMPANY_REQUIRED,
      })
      .max(30, MESSAGES.ACCOUNT.PROFILE.EDIT_NAME.FIRST_NAME_LIMIT)
      .notRequired(),
    companyName: yup
      .string()
      .trim()
      .max(100, MESSAGES.CONTACTS.CREATE.COMPANY_MAX_LENGTH)
      .test({
        test: function (value) {
          const { firstName } = this.parent;
          return !!value || !!firstName;
        },
        message: MESSAGES.CONTACTS.CREATE.FIRST_NAME_OR_COMPANY_REQUIRED,
      })
      .notRequired(),
    lastName: yup.string().trim().max(30, MESSAGES.ACCOUNT.PROFILE.EDIT_NAME.LAST_NAME_LIMIT),
    address1: yup
      .string()
      .trim()
      .max(100, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.ADDRESS_1_LIMIT)
      .required(MESSAGES.CONTACTS.CREATE.ADDRESS_1__REQUIRED),
    address2: yup
      .string()
      .trim()
      .max(100, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.ADDRESS_2_LIMIT),
    city: yup
      .string()
      .trim()
      .max(40, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.CITY_LIMIT)
      .required(MESSAGES.CONTACTS.CREATE.CITY_REQUIRED),
    state: yup
      .string()
      .trim()
      .max(40, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.STATE_LIMIT)
      .required(MESSAGES.CONTACTS.CREATE.STATE_REQUIRED),
    zip: yup
      .string()
      .trim()
      .max(10, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.ZIP_LIMIT)
      .required(MESSAGES.CONTACTS.CREATE.ZIP_REQUIRED),
    phoneNo: yup
      .string()
      .trim()
      .max(20, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.PHONE_LIMIT)
      .nullable(true),
    email: yup
      .string()
      .trim()
      .test({
        test: (value) => !value || EMAIL_REGEX.test(value),
        message: MESSAGES.AUTH.INVALID_EMAIL,
      }),

    // property address fields
    propertyAddress: yup
      .string()
      .trim()
      .max(100, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.ADDRESS_1_LIMIT),
    propertyState: yup
      .string()
      .trim()
      .max(40, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.STATE_LIMIT),
    propertyCity: yup
      .string()
      .trim()
      .max(40, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.CITY_LIMIT),
    propertyZip: yup.string().trim().max(10, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.ZIP_LIMIT),
    isReturnAddress: yup.boolean(),
    isDncList: yup.boolean().notRequired(),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      phoneNo: '',
      email: '',
      propertyAddress: '',
      propertyState: '',
      propertyCity: '',
      propertyZip: '',
      isReturnAddress: false,
      isDncList: false,
    },
    validationSchema: validationSchema,
  });

  const tags = useSelector((state) => state.contactsReducers.labels);

  // Sort the tags alphabetically
  const sortedTags = [...tags].sort((a, b) => a?.title.localeCompare(b?.title));

  const tagsOptions = sortedTags
    ?.map((tag) => {
      return {
        id: tag.id,
        title: tag.title,
      };
    })
    .concat({
      id: '-1',
      title: 'Create New Tag',
      custom: true,
      icon: PlusIconCIcon,
      func: () => setCreateTagModal(true),
    });

  const create = async (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();

      const isValid = validateForm(formik);

      if (!isValid) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const payload = { ...formik.values };
      selectedTags.length ? (payload.tags = selectedTags) : undefined;
      payload?.email?.length ? payload?.email : (payload.email = undefined);

      //<editor-fold desc="Property address fields">
      payload.propertyAddress = payload?.propertyAddress?.trim() || undefined;
      payload.propertyState = payload?.propertyState?.trim() || undefined;
      payload.propertyCity = payload?.propertyCity?.trim() || undefined;
      payload.propertyZip = payload?.propertyZip?.trim() || undefined;
      payload.isReturnAddress = payload?.isReturnAddress || false;
      payload.isDncList = isDncList;
      //</editor-fold>

      const response = await dispatch(createContact(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message));
        dispatch(fetchContacts());
        handleClose();
        handleClear();
      } else {
        isValid &&
          dispatch(failure(response?.data?.data?.errors[0]?.msg || response?.data?.message));
      }
    } catch (error) {
      Object?.keys(error).length === 0 && dispatch(failure(error.message));
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  const handleClearField = (field) => {
    formik.setFieldValue(field, '');
    // Mark as touched so validation shows on blur
    formik.setFieldTouched(field, true);
  };

  // Helper function to create endAdornment with cross button
  const getClearButtonAdornment = (fieldName) => {
    const fieldValue = formik.values[fieldName];
    return (
      fieldValue?.trim() && (
        <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
          <IconButton
            className="cpClearInputBtn smallClearIcon"
            size="small"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => handleClearField(fieldName)}
            edge="end"
            sx={{ padding: '4px' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      )
    );
  };

  const handleClear = () => {
    formik.resetForm();
    resetValidation();
    setSelectedTags([]);
    setIsDncList(false);
    // Reset all formik.touched values to false
    Object.keys(formik.initialValues).forEach((fieldName) => {
      formik.setFieldTouched(fieldName, false);
    });
    handleClose();
  };

  useEffect(() => {
    dispatch(fetchContactLabels());
  }, []);

  useEffect(() => {
    if (open) {
      // Reset form state when modal opens
      formik.resetForm();
      resetValidation();
      setSelectedTags([]);
      setIsDncList(false);
      // Reset all formik.touched values to false
      Object.keys(formik.initialValues).forEach((fieldName) => {
        formik.setFieldTouched(fieldName, false);
      });
    }

    // Cleanup function to reset v
    // alidation state when modal closes
    return () => {
      if (!open) {
        resetValidation();
      }
    };
  }, [open]);

  return (
    <>
      <OlcFormModal
        open={open}
        onClose={handleClear}
        title={MESSAGES.CONTACTS.CREATE.TITLE}
        className="createContactsModalWrapper"
        contentClassName="createContactModalContent"
        footer={
          <>
            <OlcButton variant="secondary" type="button" onClick={handleClear}>
              {MESSAGES.ORDERS.CREATE.CONTACTS.CANCEL_BUTTON}
            </OlcButton>
            <OlcButton variant="primary" type="submit" form={formId} disabled={loading}>
              {loading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '20px !important',
                    height: '20px !important',
                  }}
                />
              ) : (
                MESSAGES.ORDERS.CREATE.CONTACTS.SUBMIT_BUTTON
              )}
            </OlcButton>
          </>
        }
      >
        <Box className="contactInfoWrapper" data-testid="create-contact-scroll-body">
            <form id={formId} onSubmit={create}>
              <Box className="contactInfoHeader">
                <img src={contactInfo2CIcon} alt="info" />
                <Typography gutterBottom variant="h5">
                  {MESSAGES.CONTACTS.CREATE.CONTACT_HEADER}
                </Typography>
              </Box>
              <Box className="createContent">
                {/* first row */}
                <Grid container className="gridCustomize">
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={
                        <>
                          First Name<span className="olcRequired">*</span>
                        </>
                      }
                      type="text"
                      name="firstName"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.firstName.trimStart()}
                      updateValue={formik.handleChange}
                      isCreate={true}
                      placeholder={MESSAGES.CONTACTS.CREATE.FIRST_NAME_LABEL_PLACEHOLDER}
                      endAdornment={getClearButtonAdornment('firstName')}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={MESSAGES.CONTACTS.CREATE.LAST_NAME_LABEL}
                      name="lastName"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.lastName.trimStart()}
                      isCreate={true}
                      placeholder={MESSAGES.CONTACTS.CREATE.LAST_NAME_LABEL_PLACEHOLDER}
                      endAdornment={getClearButtonAdornment('lastName')}
                    />
                  </Grid>
                </Grid>
                {/* second row */}
                <Grid container className="gridCustomize">
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label="Company Name"
                      name="companyName"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.companyName.trimStart()}
                      isCreate={true}
                      placeholder={MESSAGES.CONTACTS.CREATE.COMPANY_LABEL_PLACEHOLDER}
                      endAdornment={getClearButtonAdornment('companyName')}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={MESSAGES.CONTACTS.CREATE.PHONE_LABEL}
                      name="phoneNo"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.phoneNo.trimStart()}
                      isCreate={true}
                      placeholder={MESSAGES.CONTACTS.CREATE.PHONE_LABEL_PLACEHOLDER}
                      endAdornment={getClearButtonAdornment('phoneNo')}
                    />
                  </Grid>
                </Grid>
                {/* third row */}
                <Grid container className="gridCustomize">
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={MESSAGES.CONTACTS.CREATE.EMAIL_LABEL}
                      name="email"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.email.trimStart()}
                      isCreate={true}
                      placeholder={MESSAGES.CONTACTS.CREATE.EMAIL_LABEL_PLACEHOLDER}
                      endAdornment={getClearButtonAdornment('email')}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={MESSAGES.CONTACTS.CREATE.TAGS_LABEL}
                      name="tags"
                      isMultiSelect={true}
                      options={tagsOptions}
                      value={selectedTags}
                      setValue={setSelectedTags}
                      isCreate={true}
                    />
                  </Grid>
                </Grid>
                <Grid container className="gridCustomize">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Box className="dncCheckboxWrapper">
                      <FormControlLabel
                        className="dncCheck"
                        control={
                          <Checkbox
                            className={`customCheckbox checked`}
                            icon={<img src={UncheckedCIcon} alt="unchecked" />}
                            style={{ pointerEvents: 'auto' }}
                            checked={isDncList}
                            onChange={(e) => {
                              const newDncValue = e.target.checked;
                              setIsDncList(newDncValue);
                              // If DNC is being checked and return address is already checked, uncheck return address
                              if (newDncValue && formik.values.isReturnAddress) {
                                formik.setFieldValue('isReturnAddress', false);
                              }
                            }}
                            checkedIcon={<img src={CheckedCIcon} alt="checked" />}
                            disableRipple={true}
                          />
                        }
                        label={MESSAGES.CONTACTS.IS_DNC_LIST}
                      />
                      <Tooltip
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                              maxWidth: '181px',
                              padding: '8px !important',
                            },
                          },
                        }}
                        title="Excludes this contact from all future mailings."
                        placement="bottom-start"
                      >
                        <img src={tooltipCIcon} className="tooltipIcon" alt="info" />
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
                {/* fourth row */}
                <Divider sx={{ margin: '20px 0', width: '100%' }} />
                <Box className="contactInfoHeader">
                  <img src={mailingInfoCIcon} alt="info" />
                  <Typography gutterBottom variant="h5">
                    {MESSAGES.CONTACTS.CREATE.MAILING_HEADER}
                  </Typography>
                </Box>
                <Grid container className="gridCustomize">
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={
                        <>
                          Mailing Address 1<span className="olcRequired">*</span>
                        </>
                      }
                      name="address1"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.address1.trimStart()}
                      isCreate={true}
                      placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.NEW_ADDRESS_1_LABEL}`}
                      endAdornment={getClearButtonAdornment('address1')}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={MESSAGES.CONTACTS.CREATE.ADDRESS_2_LABEL}
                      name="address2"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.address2.trimStart()}
                      isCreate={true}
                      placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.NEW_ADDRESS_2_LABEL}`}
                      endAdornment={getClearButtonAdornment('address2')}
                    />
                  </Grid>
                </Grid>
                {/* fifth row */}
                <Grid container className="gridCustomize">
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={
                        <>
                          Mailing City<span className="olcRequired">*</span>
                        </>
                      }
                      name="city"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.city.trimStart()}
                      isCreate={true}
                      placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.NEW_CITY_LABEL}`}
                      endAdornment={getClearButtonAdornment('city')}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={
                        <>
                          Mailing State<span className="olcRequired">*</span>
                        </>
                      }
                      name="state"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.state.trimStart()}
                      isCreate={true}
                      placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.NEW_STATE_LABEL}`}
                      endAdornment={getClearButtonAdornment('state')}
                    />
                  </Grid>
                </Grid>
                {/* sixth row */}
                <Grid container className="gridCustomize">
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <ModalInput
                      label={
                        <>
                          Mailing Zip Code<span className="olcRequired">*</span>
                        </>
                      }
                      name="zip"
                      type="text"
                      formik={formik}
                      realTimeValidation={realTimeValidation}
                      value={formik.values.zip.trimStart()}
                      isCreate={true}
                      placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.NEW_ZIP_LABEL}`}
                      endAdornment={getClearButtonAdornment('zip')}
                    />
                  </Grid>
                </Grid>

                {isPropertyAddressEnabled && (
                  <>
                    <Divider sx={{ margin: '20px 0', width: '100%' }} />
                    <Box className="contactInfoHeader">
                      <img src={propertyInfoCIcon} alt="info" />
                      <Typography gutterBottom variant="h5">
                        {MESSAGES.CONTACTS.CREATE.PROPERTY_HEADER}
                      </Typography>
                    </Box>
                    {/* sixth row */}
                    <Grid container className="gridCustomize">
                      <Grid item lg={6} md={6} sm={6} xs={12}>
                        <ModalInput
                          label={MESSAGES.CONTACTS.CREATE.PROPERTY_ADDRESS}
                          name="propertyAddress"
                          type="text"
                          formik={formik}
                          realTimeValidation={realTimeValidation}
                          value={formik.values.propertyAddress.trimStart()}
                          isCreate={true}
                          placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.PROPERTY_ADDRESS}`}
                          endAdornment={getClearButtonAdornment('propertyAddress')}
                        />
                      </Grid>
                      <Grid item lg={6} md={6} sm={6} xs={12}>
                        <ModalInput
                          label={MESSAGES.CONTACTS.CREATE.PROPERTY_CITY}
                          name="propertyCity"
                          type="text"
                          formik={formik}
                          realTimeValidation={realTimeValidation}
                          value={formik.values.propertyCity.trimStart()}
                          isCreate={true}
                          placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.PROPERTY_CITY}`}
                          endAdornment={getClearButtonAdornment('propertyCity')}
                        />
                      </Grid>
                    </Grid>
                    {/* seventh row */}
                    <Grid container className="gridCustomize">
                      <Grid item lg={6} md={6} sm={6} xs={12}>
                        <ModalInput
                          label={MESSAGES.CONTACTS.CREATE.PROPERTY_STATE}
                          name="propertyState"
                          type="text"
                          formik={formik}
                          realTimeValidation={realTimeValidation}
                          value={formik.values.propertyState.trimStart()}
                          isCreate={true}
                          placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.PROPERTY_STATE}`}
                          endAdornment={getClearButtonAdornment('propertyState')}
                        />
                      </Grid>
                      <Grid item lg={6} md={6} sm={6} xs={12}>
                        <ModalInput
                          label={MESSAGES.CONTACTS.CREATE.PROPERTY_ZIP}
                          name="propertyZip"
                          type="text"
                          formik={formik}
                          realTimeValidation={realTimeValidation}
                          value={formik.values.propertyZip.trimStart()}
                          isCreate={true}
                          placeholder={`Enter ${MESSAGES.CONTACTS.CREATE.PROPERTY_ZIP}`}
                          endAdornment={getClearButtonAdornment('propertyZip')}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>
            </form>
        </Box>
      </OlcFormModal>
      <CreateTagModal
        openModal={createTagModal}
        handleCloseModal={() => setCreateTagModal(false)}
        setSelectedTags={setSelectedTags}
        setTags={true}
      />
    </>
  );
};

export default CreateContactModal;
